// Display names + accent colours for agent + nested finster tools. The finster
// sub-agent map is ported verbatim from finster-chat's tool-display-names.ts so
// every data-source tool gets a friendly label; the PowerPoint agent tools are
// added on top.

const DISPLAY_NAMES: Record<string, string> = {
  // PowerPoint agent tools
  finster_query: "Finster",
  factset_ai_query: "FactSet AI",
  apply_edits: "Apply edits",
  get_slides_info: "Inspect slides",
  insert_library_slide: "Insert library slide",
  apply_preslink: "Resolve PresLink",
  get_company_logo: "Company logo",
  get_slide_images: "Capture slides",

  // FactSet core
  get_factset_data: "FactSet Data",
  get_segments_data: "FactSet Segments",
  get_estimates_data: "FactSet Estimates",
  get_earnings_beat_miss_data: "FactSet Earnings Surprise",
  get_global_price_trend_v2: "FactSet Market Data",
  get_company_profile_data: "FactSet Company Profile",
  get_historic_events_dates: "FactSet Historic Events",
  get_upcoming_events_dates: "FactSet Upcoming Events",
  get_earnings_calendar_data: "FactSet Earnings Calendar",

  // FactSet additional
  get_company_executives: "FactSet People & Executives",
  get_person_profile_details: "FactSet Person Profile",
  get_ma_deals: "FactSet M&A Deals",
  get_etf_details: "FactSet ETF",
  get_private_company_profile: "FactSet Private Company",
  get_private_company_financial_statements: "FactSet Private Company Financials",
  get_private_company_financial_metrics: "FactSet Private Company Metrics",
  get_fund_holdings: "FactSet Fund Holdings",
  get_security_holders: "FactSet Security Holders",
  get_ownership_transactions: "FactSet Ownership Transactions",
  get_economic_events: "FactSet Economic Events",
  get_streetaccount_news: "FactSet StreetAccount News",
  query_amr: "FactSet AMR",
  get_rbics_entity_focus: "FactSet RBICS Entity Focus",
  get_rbics_entity_revenue: "FactSet RBICS Entity Revenue",
  get_rbics_taxonomy_structure: "FactSet RBICS Taxonomy Structure",
  get_rbics_industry_focus: "FactSet RBICS Industry Focus",
  get_rbics_industry_revenue: "FactSet RBICS Industry Revenue",
  get_rbics_trade_names: "FactSet RBICS Trade Names",

  // Official company data
  get_sec_data: "SEC Filings",
  get_transcripts_data: "Earnings Transcripts",
  get_company_website_data: "Company Website",
  get_reports_data: "Investor Relations",
  get_private_company_reports_data: "Private Company IR",

  // Research & third-party
  get_morningstar_data: "Morningstar Reports",
  get_bluematrix_data: "Broker Research",
  get_industry_research_data: "Industry Research",
  get_third_bridge_transcripts_data: "Expert Network",
  get_web_data: "Web Search",

  // CapIQ
  get_capiq_data: "CapIQ Estimates",
  get_capiq_fundamentals_data: "CapIQ Fundamentals",

  // ADIA
  get_fund_info: "Fund Info",
  get_fund_returns: "Fund Returns",
  get_mac_sector_pnl: "MAC Sector P&L",
  get_sector_metrics: "Market Data",
  get_adia_crm_data: "ADIA CRM Documents",
  get_adia_investment_proposal_documents: "Investment Proposal Documents",
  get_adia_audited_financial_statements: "Audited Financial Statements",

  // Private markets
  get_private_markets_data: "Private Markets",
  get_private_markets_deals: "Private Markets Deals",

  // User files
  get_user_file_data: "User Files",
  summarize_user_file_data: "User File Summary",

  // Utility
  company_lookup: "Company Lookup",
  company_lookup_filtered: "Company Lookup",
  all_company_search: "Company Search",
  get_query_history: "Session History",
};

export function getAgentToolDisplayName(tool: string): string {
  const known = DISPLAY_NAMES[tool];
  if (known) return known;
  return tool
    .replace(/^get_/, "")
    .replace(/_data$/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const SLIDE_TOOLS = new Set([
  "apply_edits",
  "get_slides_info",
  "insert_library_slide",
  "apply_preslink",
  "get_company_logo",
  "get_slide_images",
]);

const FACTSET_TOOLS = new Set([
  "factset_ai_query",
  "get_factset_data",
  "get_segments_data",
  "get_estimates_data",
  "get_earnings_beat_miss_data",
  "get_global_price_trend_v2",
  "get_company_profile_data",
  "get_historic_events_dates",
  "get_upcoming_events_dates",
  "get_earnings_calendar_data",
  "get_company_executives",
  "get_person_profile_details",
  "get_ma_deals",
  "get_etf_details",
  "get_private_company_profile",
  "get_private_company_financial_statements",
  "get_private_company_financial_metrics",
  "get_fund_holdings",
  "get_security_holders",
  "get_ownership_transactions",
  "get_economic_events",
  "get_streetaccount_news",
  "query_amr",
  "get_rbics_entity_focus",
  "get_rbics_entity_revenue",
  "get_rbics_taxonomy_structure",
  "get_rbics_industry_focus",
  "get_rbics_industry_revenue",
  "get_rbics_trade_names",
]);

/** Slide-mutation / observation tools (internal mechanics, never shown as failures). */
export function isSlideTool(tool: string): boolean {
  return SLIDE_TOOLS.has(tool);
}

/** Tailwind text-colour class for a tool's completed status icon / accent. */
export function getAgentToolColorClass(tool: string): string {
  if (isSlideTool(tool)) return "text-muted-foreground";
  if (tool === "finster_query" || FACTSET_TOOLS.has(tool)) return "text-primary";
  return "text-info";
}

/** Research tools whose results are shown inline (answer + citations). */
export function isResearchTool(tool: string): boolean {
  return tool === "finster_query" || tool === "factset_ai_query";
}
